// El expediente del análisis de cartera con IA (postventa). El core-api
// CALCULA los hallazgos (apps/core-api/src/lib/hallazgos) y la IA sólo
// los redacta: cada afirmación del memo cita un hallazgo por su `key`
// ([[H1]]) y toda cifra que se ve en pantalla sale de acá, no del texto
// del modelo. Superficie hermana del panel táctico de insights
// (docs/auditoria-insights-estrategicos.md §7), sin su ranking ni sus
// señales.
//
// Ningún campo carga texto del propietario ni del contratista, nombres de
// personas ni contactos: `facts` y `headline` son plantillas es-CL con
// números, códigos, proyectos y el SEUDÓNIMO del contratista («Contratista
// A»). El nombre real no se guarda ni viaja a la IA: core-api lo resuelve
// al leer (`contratistas` del informe) y el dashboard lo pone en pantalla.
import { z } from "zod";
export const HALLAZGO_KINDS = [
    "proyectos",
    "contratistas",
    "evitables",
    "segmentos",
    "satisfaccion",
];
/** Rótulo de la familia de un hallazgo (la ficha del dashboard). */
export const HALLAZGO_KIND_LABEL = {
    proyectos: "Proyectos",
    contratistas: "Contratistas",
    evitables: "Reclamos evitables",
    segmentos: "Tipologías",
    satisfaccion: "Satisfacción del propietario",
};
/** Los temas del memo: cada sección habla de uno. Un tema agrupa una o
 *  más familias de hallazgos (los segmentos por tipología se narran con
 *  los proyectos), así que el memo no crece con cada detector nuevo: un
 *  detector nuevo se suma a un tema existente. `garantias` todavía no
 *  tiene detector que lo alimente. */
export const MEMO_TEMAS = [
    "proyectos",
    "contratistas",
    "evitables",
    "garantias",
    "satisfaccion",
];
export const MEMO_TEMA_LABEL = {
    proyectos: "Proyectos",
    contratistas: "Contratistas",
    evitables: "Reclamos evitables",
    garantias: "Garantías",
    satisfaccion: "Satisfacción",
};
/** El tema del memo en que se narra cada familia de hallazgos. */
export const HALLAZGO_TEMA = {
    proyectos: "proyectos",
    contratistas: "contratistas",
    evitables: "evitables",
    // Una tipología es una parte de un proyecto: su lección («revisar el
    // diseño de la tipología C1 en el próximo proyecto») va con las de los
    // proyectos, no en una sección propia.
    segmentos: "proyectos",
    // Lo que dice el propietario es otra lección que lo que cuentan las
    // observaciones: mezclarlo con «Proyectos» juntaría opinión con conteo.
    satisfaccion: "satisfaccion",
};
// ── Tramos de las encuestas (ADR-0170) ───────────────────────────────
//
// Las encuestas entran al expediente sólo en tramos: el valor exacto no
// se escribe en ningún campo, para que no se pueda despejar la respuesta
// de un propietario. `contrast` y `series` llevan el BORDE INFERIOR del
// tramo y la `unit` dice cuál es; el dashboard muestra el tramo entero.
export const TRAMO_NPS = 10;
export const TRAMO_VISITA = 0.5;
export const UNIDAD_TRAMO_NPS = "NPS, tramo de 10 puntos";
export const UNIDAD_TRAMO_VISITA = "nota de 1 a 5, tramo de medio punto";
/** El borde inferior del tramo de `x`. El tope cae en el último tramo
 *  (NPS 100 → 90, nota 5 → 4,5). */
export function bordeTramo(x, ancho, tope) {
    const borde = Math.floor(x / ancho) * ancho;
    return Math.round(Math.min(borde, tope - ancho) * 10) / 10;
}
export function bordeTramoNps(nps) {
    return bordeTramo(nps, TRAMO_NPS, 100);
}
export function bordeTramoVisita(nota) {
    return bordeTramo(nota, TRAMO_VISITA, 5);
}
const signo = (n) => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : "0");
const decimal = (n) => n.toFixed(1).replace(".", ",");
/** «entre +10 y +20», «entre −20 y −10», «entre 3,5 y 4,0». `null` si la
 *  unidad no es de un tramo. */
export function etiquetaTramo(unit, borde) {
    if (unit === UNIDAD_TRAMO_NPS)
        return `entre ${signo(borde)} y ${signo(borde + TRAMO_NPS)}`;
    if (unit === UNIDAD_TRAMO_VISITA) {
        return `entre ${decimal(borde)} y ${decimal(borde + TRAMO_VISITA)}`;
    }
    return null;
}
/** El rango de la escala de un tramo, para dibujar barras con valores
 *  negativos (el NPS va de −100 a 100). `null` si no es un tramo. */
export function escalaTramo(unit) {
    if (unit === UNIDAD_TRAMO_NPS)
        return { min: -100, max: 100 };
    if (unit === UNIDAD_TRAMO_VISITA)
        return { min: 1, max: 5 };
    return null;
}
// ── Seudónimos de contratistas ───────────────────────────────────────
//
// Un contratista puede ser una persona natural: el expediente que se
// guarda y que lee la IA lo nombra con un seudónimo por expediente
// («Contratista A», «Contratista B»…), con letras y no dígitos para que
// `sanearMemo` no lo confunda con una cifra. El expediente guarda la
// tabla seudónimo → id interno del contratista (un uuid, no un nombre);
// el nombre se resuelve sólo al leer, dentro de la organización.
export const CONTRATISTA_SEUDONIMO_PREFIJO = "Contratista";
/** «Contratista A», «Contratista B», … «Contratista Z», «Contratista AA»… */
export function seudonimoContratista(i) {
    let letras = "";
    let n = i;
    do {
        letras = String.fromCharCode(65 + (n % 26)) + letras;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return `${CONTRATISTA_SEUDONIMO_PREFIJO} ${letras}`;
}
export const contratistaSeudonimoSchema = z
    .string()
    .regex(new RegExp(`^${CONTRATISTA_SEUDONIMO_PREFIJO} [A-Z]+$`));
/** El seudónimo como palabra completa: «Contratista A» no calza dentro de
 *  «Contratista AB» ni de «Contratista Andes». */
const SEUDONIMO_RE = new RegExp(`\\b${CONTRATISTA_SEUDONIMO_PREFIJO} [A-Z]+\\b`, "g");
/** Cambia cada seudónimo de `texto` por el nombre que `nombres` le da. Un
 *  seudónimo sin nombre (el contratista se borró) queda como está. */
export function nombrarContratistas(texto, nombres) {
    if (!nombres)
        return texto;
    return texto.replace(SEUDONIMO_RE, (s) => nombres[s] ?? s);
}
/** Un hallazgo con los seudónimos cambiados por nombres en todos sus
 *  textos (titular, hechos, rótulos del gráfico, advertencias). Las
 *  claves, ids y cifras no cambian. */
export function hallazgoConNombres(h, nombres) {
    if (!nombres || Object.keys(nombres).length === 0)
        return h;
    const n = (t) => nombrarContratistas(t, nombres);
    return {
        ...h,
        question: n(h.question),
        headline: n(h.headline),
        facts: h.facts.map(n),
        contrast: {
            ...h.contrast,
            unit: n(h.contrast.unit),
            referenceLabel: n(h.contrast.referenceLabel),
        },
        evidence: {
            ...h.evidence,
            nUnit: n(h.evidence.nUnit),
            series: h.evidence.series.map((p) => ({ ...p, label: n(p.label) })),
            seriesLabels: {
                value: n(h.evidence.seriesLabels.value),
                reference: n(h.evidence.seriesLabels.reference),
            },
        },
        caveats: h.caveats.map(n),
    };
}
/** Cuánto aguanta la evidencia. Lo decide el cálculo con reglas fijas
 *  por tipo de hallazgo, nunca el modelo. */
export const HALLAZGO_CONFIDENCES = ["pista", "patron_temprano", "patron_confirmado"];
export const HALLAZGO_CONFIDENCE_LABEL = {
    pista: "Pista",
    patron_temprano: "Patrón temprano",
    patron_confirmado: "Patrón confirmado",
};
/** Un punto del mini gráfico: el valor del sujeto contra la referencia. */
export const hallazgoSeriePointSchema = z
    .object({
    label: z.string(),
    value: z.number().finite(),
    reference: z.number().finite().nullable(),
})
    .strict();
export const hallazgoSchema = z
    .object({
    /** Clave de cita del memo: «H1», «H2»… por orden de relevancia. */
    key: z.string().regex(/^H\d+$/),
    /** Clave determinista del sujeto (`proyectos:<id>`,
     *  `contratistas:<id>:<especialidad>`…): la misma condición produce
     *  la misma clave entre corridas. */
    id: z.string().min(1),
    kind: z.enum(HALLAZGO_KINDS),
    /** Proyecto del hallazgo; null si cruza proyectos. */
    projectId: z.string().uuid().nullable(),
    /** La pregunta de negocio que contesta. */
    question: z.string(),
    /** La frase con la cifra verificable. */
    headline: z.string(),
    /** Hechos es-CL con cifras. Lo único que el memo puede afirmar. */
    facts: z.array(z.string()),
    contrast: z
        .object({
        value: z.number().finite(),
        reference: z.number().finite(),
        /** value / reference; null si la referencia es 0. */
        ratio: z.number().finite().nullable(),
        /** Unidad de `value` y `reference` («obs. por unidad», «%»…). */
        unit: z.string(),
        /** Contra qué se compara («resto de la cartera a la misma edad»). */
        referenceLabel: z.string(),
    })
        .strict(),
    evidence: z
        .object({
        /** Tamaño de la muestra detrás del hallazgo. */
        n: z.number().int().nonnegative(),
        /** Qué cuenta `n` («unidades», «reaperturas», «observaciones»). */
        nUnit: z.string(),
        /** Proyectos que aportan casos; null si no se cuenta por proyecto. */
        projects: z.number().int().nonnegative().nullable(),
        series: z.array(hallazgoSeriePointSchema),
        seriesLabels: z.object({ value: z.string(), reference: z.string() }).strict(),
        /** Enlace a los casos en el dashboard; null si no hay una lista
         *  que los muestre juntos. */
        href: z.string().startsWith("/").nullable(),
    })
        .strict(),
    confidence: z.enum(HALLAZGO_CONFIDENCES),
    /** Advertencias que el memo debe conservar («atribución aproximada»). */
    caveats: z.array(z.string()),
})
    .strict();
export const postventaDossierScopeSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("cartera") }).strict(),
    z
        .object({
        kind: z.literal("proyecto"),
        projectId: z.string().uuid(),
        projectName: z.string(),
    })
        .strict(),
]);
/** Versión del expediente que se guarda. Un informe guardado con otra
 *  versión no se muestra: el GET lo trata como ausente. */
export const POSTVENTA_DOSSIER_VERSION = 1;
export const postventaDossierSchema = z
    .object({
    version: z.literal(POSTVENTA_DOSSIER_VERSION),
    generatedAt: z.string().datetime(),
    scope: postventaDossierScopeSchema,
    /** «Analizando N observaciones en M unidades de K proyectos». */
    totals: z
        .object({
        observations: z.number().int().nonnegative(),
        units: z.number().int().nonnegative(),
        projects: z.number().int().nonnegative(),
    })
        .strict(),
    projects: z.array(z
        .object({
        id: z.string().uuid(),
        name: z.string(),
        units: z.number().int().nonnegative(),
        observations: z.number().int().nonnegative(),
        /** Meses desde la primera y la última entrega; null sin actas. */
        monthsSinceFirstDelivery: z.number().int().nonnegative().nullable(),
        monthsSinceLastDelivery: z.number().int().nonnegative().nullable(),
        /** Si entró a la comparación entre proyectos a igual edad. */
        compared: z.boolean(),
    })
        .strict()),
    /** Mezcla por especialidad de las observaciones del alcance. */
    specialties: z.array(z
        .object({
        code: z.string(),
        name: z.string(),
        observations: z.number().int().nonnegative(),
        /** Porcentaje entero del total. */
        sharePct: z.number().int().min(0).max(100),
    })
        .strict()),
    hallazgos: z.array(hallazgoSchema),
    /** Lo que el cálculo no pudo comparar y por qué (es-CL). */
    notes: z.array(z.string()),
    /** Seudónimo → id interno del contratista, para los contratistas que
     *  nombran los hallazgos. Ausente si ninguno nombra a uno (y en los
     *  expedientes guardados antes de los seudónimos). No viaja a la IA. */
    contratistas: z.record(contratistaSeudonimoSchema, z.string().uuid()).optional(),
})
    .strict();
// ── El memo ───────────────────────────────────────────────────────────
//
// La IA devuelve un BORRADOR (`portfolioMemoDraftSchema`, permisivo) y
// `sanearMemo` lo convierte en el MEMO que se guarda y se muestra
// (`portfolioMemoSchema`). El saneamiento es determinista y vive acá, no
// en ai-svc, para que core-api pueda repetirlo sobre lo que recibe: un
// memo saneado vuelve idéntico de una segunda pasada.
export const PORTFOLIO_MEMO_SECCION_CAP = 4;
export const PORTFOLIO_MEMO_LECCION_CAP = 3;
/** Los hallazgos cuyas cifras puede usar el titular: el memo siempre
 *  los cubre, así que el titular no destaca algo que el cuerpo calla. */
const TITULAR_CLAVES = ["H1", "H2", "H3"];
/** Una cita del memo: «[[H1]]». */
const CITA_RE = /\[\[(H\d+)\]\]/g;
export const portfolioMemoSeccionSchema = z
    .object({
    /** El tema de la sección. Los memos guardados antes de los temas
     *  traen un `kind` de hallazgo, que es también un tema. */
    tipo: z.enum(MEMO_TEMAS),
    titulo: z.string().min(1),
    /** Prosa con citas `[[Hk]]` al final de cada afirmación. */
    texto: z.string().min(1),
    /** Las claves citadas en `texto`, en orden de aparición. Las deriva
     *  el saneamiento del texto, no las declara el modelo. */
    citas: z.array(z.string().regex(/^H\d+$/)).min(1),
    recomendacion: z.string().min(1).nullable(),
    /** Un cargo («Jefatura de postventa»), nunca una persona. */
    responsable: z.string().min(1).nullable(),
})
    .strict();
export const portfolioMemoLeccionSchema = z
    .object({
    texto: z.string().min(1),
    citas: z.array(z.string().regex(/^H\d+$/)),
})
    .strict();
export const portfolioMemoSchema = z
    .object({
    titular: z.string().min(1),
    secciones: z.array(portfolioMemoSeccionSchema).max(PORTFOLIO_MEMO_SECCION_CAP),
    /** Lecciones para el próximo proyecto. No se guardan aparte. */
    lecciones: z.array(portfolioMemoLeccionSchema).max(PORTFOLIO_MEMO_LECCION_CAP),
})
    .strict();
/** Lo que el modelo devuelve. Permisivo a propósito: un `tipo` raro o
 *  una recomendación ausente degradan esa parte del memo en el
 *  saneamiento, no tumban la llamada. */
export const portfolioMemoDraftSchema = z.object({
    titular: z.string(),
    secciones: z
        .array(z.object({
        tipo: z.string(),
        titulo: z.string(),
        texto: z.string(),
        recomendacion: z.string().nullish(),
        responsable: z.string().nullish(),
    }))
        .max(8),
    lecciones: z.array(z.string()).max(8).default([]),
});
/** Qué quitó el saneamiento. Sólo conteos y claves: las frases pueden
 *  nombrar contratistas y no van a los logs. */
export const portfolioMemoDescartesSchema = z
    .object({
    /** Claves citadas que no existen en el expediente. */
    citasInexistentes: z.array(z.string()),
    /** Frases quitadas por traer una cifra que el expediente no tiene. */
    frasesConCifraAjena: z.number().int().nonnegative(),
    /** Secciones quitadas por quedar sin texto o sin ninguna cita. */
    seccionesSinRespaldo: z.number().int().nonnegative(),
    /** El titular traía una cifra ajena y se reemplazó por el del primer
     *  hallazgo. */
    titularReemplazado: z.boolean(),
})
    .strict();
/** Las cifras de un texto, normalizadas a número. «1,8» y «1.8» son 1,8;
 *  «1.162» es mil ciento sesenta y dos. Las citas `[[Hk]]` no cuentan. */
export function cifrasDe(texto) {
    const out = [];
    for (const m of texto.replace(CITA_RE, " ").matchAll(/\d+(?:[.,]\d+)*/g)) {
        const raw = m[0];
        const n = /^\d{1,3}(?:\.\d{3})+$/.test(raw)
            ? Number(raw.replace(/\./g, ""))
            : Number(raw.replace(/\./g, "").replace(",", "."));
        if (Number.isFinite(n))
            out.push(n);
    }
    return out;
}
/** Campos de identidad: sus dígitos (uuids, «H3», rutas) no son cifras
 *  que el memo pueda narrar. */
const CAMPOS_SIN_CIFRAS = new Set(["key", "id", "projectId", "href", "generatedAt", "version"]);
function cifrasDeValor(v, out) {
    if (typeof v === "number")
        out.add(v);
    else if (typeof v === "string")
        for (const n of cifrasDe(v))
            out.add(n);
    else if (Array.isArray(v))
        for (const x of v)
            cifrasDeValor(x, out);
    else if (v && typeof v === "object") {
        for (const [k, x] of Object.entries(v))
            if (!CAMPOS_SIN_CIFRAS.has(k))
                cifrasDeValor(x, out);
    }
    return out;
}
/** Las cifras que cada parte del expediente respalda: las de cada
 *  hallazgo por su clave, y las del contexto (totales, proyectos,
 *  especialidades, notas), que cualquier frase puede usar. */
export function cifrasDelExpediente(dossier) {
    const { totals, projects, specialties, notes } = dossier;
    return {
        contexto: cifrasDeValor({ totals, projects, specialties, notes }, new Set()),
        porHallazgo: new Map(dossier.hallazgos.map((h) => [h.key, cifrasDeValor(h, new Set())])),
    };
}
function citasDe(texto) {
    return [...new Set([...texto.matchAll(CITA_RE)].map((m) => m[1]))];
}
/** Una frase termina en . ! ? seguido de espacio y de mayúscula o cifra
 *  («31 % de los reclamos…»): así «obs. por unidad» no parte la frase. */
function frases(texto) {
    return texto
        .split(/(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÑ¿¡«\d])/)
        .map((f) => f.trim())
        .filter(Boolean);
}
/**
 * Del borrador del modelo al memo que se muestra:
 *   1. quita las citas a claves que el expediente no tiene;
 *   2. quita cada frase con una cifra que no respaldan los hallazgos que
 *      ESA frase cita ni el contexto del expediente (la IA narra, no
 *      calcula: una suma o un porcentaje propio es una cifra ajena aunque
 *      sea aritméticamente correcta, y una cifra de H1 citada como H3
 *      también). El titular no cita: puede usar las de H1–H3. La
 *      recomendación, las de su sección;
 *   3. quita la sección que quedó sin texto o sin cita, y las que pasan
 *      del tope; deriva `citas` del texto;
 *   4. si el titular trae una cifra que no respaldan el contexto ni los
 *      tres primeros hallazgos (H1–H3, los que el memo siempre cubre),
 *      usa el del primer hallazgo.
 * Idempotente: sanear un memo ya saneado lo devuelve igual.
 */
export function sanearMemo(draft, dossier) {
    const claves = new Set(dossier.hallazgos.map((h) => h.key));
    const cifras = cifrasDelExpediente(dossier);
    const citasInexistentes = new Set();
    let frasesConCifraAjena = 0;
    let seccionesSinRespaldo = 0;
    /** ¿Toda cifra de `t` la respaldan el contexto o los hallazgos `keys`? */
    const verificable = (t, keys) => cifrasDe(t).every((n) => cifras.contexto.has(n) || keys.some((k) => cifras.porHallazgo.get(k)?.has(n)));
    const limpiar = (texto) => {
        const sinCitasAjenas = texto.replace(CITA_RE, (cita, key) => {
            if (claves.has(key))
                return cita;
            citasInexistentes.add(key);
            return "";
        });
        return frases(sinCitasAjenas)
            .filter((f) => {
            if (verificable(f, citasDe(f)))
                return true;
            frasesConCifraAjena++;
            return false;
        })
            .map((f) => f.replace(/\s+([.,;:])/g, "$1").replace(/\s{2,}/g, " "))
            .join(" ")
            .trim();
    };
    const opcional = (t, keys) => {
        const v = t?.trim();
        if (!v)
            return null;
        if (verificable(v, keys))
            return v;
        frasesConCifraAjena++;
        return null;
    };
    const tipoDe = (tipo, citas) => {
        if (MEMO_TEMAS.includes(tipo))
            return tipo;
        const primera = dossier.hallazgos.find((h) => h.key === citas[0]);
        return primera ? HALLAZGO_TEMA[primera.kind] : null;
    };
    const secciones = [];
    for (const s of draft.secciones) {
        const texto = limpiar(s.texto);
        const citas = citasDe(texto);
        const tipo = tipoDe(s.tipo.trim(), citas);
        const titulo = s.titulo.trim();
        if (!texto || citas.length === 0 || !tipo || !titulo || !verificable(titulo, citas)) {
            seccionesSinRespaldo++;
            continue;
        }
        if (secciones.length >= PORTFOLIO_MEMO_SECCION_CAP) {
            seccionesSinRespaldo++;
            continue;
        }
        secciones.push({
            tipo,
            titulo,
            texto,
            citas,
            recomendacion: opcional(s.recomendacion, citas),
            responsable: opcional(s.responsable, citas),
        });
    }
    const lecciones = draft.lecciones
        .map((l) => limpiar(l))
        .filter(Boolean)
        .slice(0, PORTFOLIO_MEMO_LECCION_CAP)
        .map((texto) => ({ texto, citas: citasDe(texto) }));
    const titularDraft = draft.titular
        .replace(CITA_RE, "")
        .replace(/\s{2,}/g, " ")
        .trim();
    const titularReemplazado = !titularDraft ||
        !verificable(titularDraft, TITULAR_CLAVES.filter((k) => claves.has(k)));
    const titular = titularReemplazado ? (dossier.hallazgos[0]?.headline ?? "") : titularDraft;
    return {
        memo: portfolioMemoSchema.parse({ titular, secciones, lecciones }),
        descartes: {
            citasInexistentes: [...citasInexistentes],
            frasesConCifraAjena,
            seccionesSinRespaldo,
            titularReemplazado,
        },
    };
}
// ── Contrato interno core-api → ai-svc (POST /internal/portfolio-brief) ──
/** Escalera de timeouts, como la de classify-finding (ADR-0119): 50 s en
 *  el proveedor sin reintentos < 52 s en la ruta de ai-svc < 55 s en
 *  core-api < 60 s en el borde de Railway. Más holgada que la de
 *  clasificación porque un memo son ~1.500 tokens de salida. */
export const INTERNAL_PORTFOLIO_BRIEF_CALLER_TIMEOUT_MS = 55_000;
export const internalPortfolioBriefRequest = z
    .object({
    organizationId: z.string().uuid(),
    recipeSlug: z.string().min(1),
    /** Sin hallazgos no hay nada que narrar: core-api no llama. */
    dossier: postventaDossierSchema.refine((d) => d.hallazgos.length > 0, {
        message: "el expediente no tiene hallazgos",
    }),
})
    .strict();
export const internalPortfolioBriefResponse = z
    .object({
    /** Modelo que redactó («mock» bajo MockProvider). */
    model: z.string(),
    memo: portfolioMemoSchema,
    usage: z
        .object({
        inputTokens: z.number().int().nonnegative(),
        outputTokens: z.number().int().nonnegative(),
    })
        .strict(),
    descartes: portfolioMemoDescartesSchema,
})
    .strict();
// ── Wire del dashboard: GET/POST /postventa-dashboard/analisis ─────────
//
// POST calcula el expediente, le pide el memo a ai-svc, lo sanea otra vez
// contra el expediente propio y guarda el último informe por alcance. GET
// devuelve ese último informe, o null. El alcance es la cartera o un
// proyecto (`assetGroupId`), siempre dentro de lo que el llamador ve: un
// jefe de proyecto y la gerencia no comparten informe aunque pidan el
// mismo alcance.
/** Análisis con IA por organización en una ventana móvil de 24 h. Cada
 *  uno es una llamada al modelo; los que no llaman (expediente sin
 *  hallazgos) no cuentan. */
export const POSTVENTA_ANALISIS_CUOTA_24H = 10;
/** Retención del informe guardado (DPIA-lite 2026-10-26, decisión del
 *  founder): un informe que nadie regeneró en este plazo se trata como
 *  ausente al leerlo y la purga lo borra. Lleva datos relacionables
 *  (contratistas, celdas que llevan a una unidad). */
export const POSTVENTA_ANALISIS_RETENCION_MESES = 12;
/** Cuánto tiene que moverse la cifra principal de un hallazgo (o su
 *  factor contra la referencia) para que cuente como otra conclusión. */
export const CAMBIO_MATERIAL_CIFRA = 0.1;
/** ¿Regenerar diría algo distinto? El análisis lee patrones de cartera,
 *  no observaciones sueltas: una observación más o un caso cerrado mueven
 *  los totales pero no las conclusiones. Cambia la conclusión cuando
 *  aparece o desaparece un hallazgo, cuando cambia su nivel de evidencia,
 *  o cuando su cifra principal o su factor se mueven más de
 *  CAMBIO_MATERIAL_CIFRA. Los hallazgos se comparan por `id` (el sujeto),
 *  no por su clave de cita, que depende del orden. */
/** ¿Las cifras del expediente nuevo difieren de las guardadas, aunque no
 *  cambie ninguna conclusión? Compara todo el expediente salvo
 *  `generatedAt`, con las claves ordenadas (la columna jsonb no conserva
 *  el orden). Sirve para avisar que las cifras que se ven son del informe
 *  anterior. */
export function cifrasDistintas(previo, nuevo) {
    const estable = (d) => {
        const ordenar = (v) => {
            if (Array.isArray(v))
                return v.map(ordenar);
            if (v !== null && typeof v === "object") {
                return Object.fromEntries(Object.keys(v)
                    .sort()
                    .map((k) => [k, ordenar(v[k])]));
            }
            return v;
        };
        const { generatedAt: _generatedAt, ...resto } = d;
        return JSON.stringify(ordenar(resto));
    };
    return estable(previo) !== estable(nuevo);
}
export function cambioMaterial(previo, nuevo) {
    if (previo.hallazgos.length !== nuevo.hallazgos.length)
        return true;
    const antes = new Map(previo.hallazgos.map((h) => [h.id, h]));
    const seMueve = (a, b) => {
        if (a === null || b === null)
            return a !== b;
        if (a === b)
            return false;
        return Math.abs(b - a) > CAMBIO_MATERIAL_CIFRA * Math.abs(a);
    };
    return nuevo.hallazgos.some((h) => {
        const p = antes.get(h.id);
        return (p === undefined ||
            p.confidence !== h.confidence ||
            seMueve(p.contrast.value, h.contrast.value) ||
            seMueve(p.contrast.ratio, h.contrast.ratio));
    });
}
export const postventaAnalisisQuery = z.object({
    /** Proyecto foco comparado contra el resto; sin él, la cartera. */
    assetGroupId: z.string().uuid().optional(),
});
export const postventaAnalisisRequest = postventaAnalisisQuery.strict();
export const postventaAnalisisSchema = z
    .object({
    id: z.string().uuid(),
    /** De acá salen las cifras, fichas, gráficos y enlaces que se ven. */
    dossier: postventaDossierSchema,
    /** null si el expediente no trajo hallazgos: no había nada que narrar
     *  y no se llamó al modelo. */
    memo: portfolioMemoSchema.nullable(),
    /** Modelo que redactó; null sin memo. */
    model: z.string().nullable(),
    createdAt: z.string().datetime(),
    /** Seudónimo → nombre corto del contratista, resuelto por core-api al
     *  leer (dentro de la organización). Un seudónimo cuyo contratista ya
     *  no existe no aparece: la pantalla deja el seudónimo. */
    contratistas: z.record(contratistaSeudonimoSchema, z.string().min(1)).optional(),
})
    .strict();
/** Un memo con los seudónimos cambiados por nombres. */
export function memoConNombres(memo, nombres) {
    if (!nombres || Object.keys(nombres).length === 0)
        return memo;
    const n = (t) => nombrarContratistas(t, nombres);
    const opt = (t) => (t === null ? null : n(t));
    return {
        titular: n(memo.titular),
        secciones: memo.secciones.map((s) => ({
            ...s,
            titulo: n(s.titulo),
            texto: n(s.texto),
            recomendacion: opt(s.recomendacion),
            responsable: opt(s.responsable),
        })),
        lecciones: memo.lecciones.map((l) => ({ ...l, texto: n(l.texto) })),
    };
}
export const postventaAnalisisResponse = z
    .object({
    analisis: postventaAnalisisSchema.nullable(),
    /** Sólo en la respuesta de «Regenerar»: true cuando el expediente
     *  recalculado no cambia ninguna conclusión del guardado
     *  (`cambioMaterial`). Se devolvió el informe que ya estaba, sin llamar
     *  al modelo ni gastar cuota. */
    sinCambios: z.boolean().optional(),
    /** Junto con `sinCambios`: true cuando las cifras recalculadas difieren
     *  de las del informe que se mantuvo (entraron observaciones, se movió
     *  una cifra bajo el umbral). Las que se ven son las del informe
     *  anterior. */
    cifrasDesactualizadas: z.boolean().optional(),
    /** Para que la pantalla avise antes de que «Regenerar» choque con el
     *  tope. `usados` cuenta la organización entera. */
    cuota: z
        .object({
        usados: z.number().int().nonnegative(),
        limite: z.number().int().positive(),
    })
        .strict(),
})
    .strict();
/** Un memo ya saneado, de vuelta a la forma de borrador, para que core-api
 *  pueda pasarlo otra vez por `sanearMemo` contra su propio expediente. */
export function memoComoBorrador(memo) {
    return {
        titular: memo.titular,
        secciones: memo.secciones.map(({ citas: _citas, ...s }) => s),
        lecciones: memo.lecciones.map((l) => l.texto),
    };
}
//# sourceMappingURL=postventa-analisis.js.map